// ============================================================================
// MÓDULO 7 - UNIDAD 12: TRAVEL & TRANSPORTATION (FINAL UNIT!)
// ============================================================================
// Nivel: B1-B2 (Upper-Intermediate)
// Temas: Transportation, airport, accommodation, travel logistics
// Gramática: COLLOCATIONS - verb + noun combinations (board a plane, etc.)
// Critical: Natural travel phrases, not literal translations!
// Vocabulary: 13 vehicles, 9 transit places, 20+ travel actions
// ============================================================================

export const M7_UNIT_12 = {
  id: "m7-u12",
  title: "Unit 12: Travel & Transportation",
  topic: "Master all transportation vocabulary, learn natural travel collocations, and navigate airports and hotels like a native speaker!",
  
  grammarImage: "/images/grammar/m7-u12-travel-collocations.jpg",
  grammarText: `
    <h3>Travel & Transportation</h3>
    
    <div class="transportation">
      <h4>Means of Transportation (13 Vehicles)</h4>
      
      <div class="land-transport">
        <h5>Land Transport:</h5>
        <ul>
          <li><strong>Car</strong> - Carro/Coche</li>
          <li><strong>Taxi</strong> - Taxi</li>
          <li><strong>Bus</strong> - Autobús (city/local)</li>
          <li><strong>Coach</strong> - Autocar (long-distance bus)</li>
          <li><strong>Train</strong> - Tren</li>
          <li><strong>Tram</strong> - Tranvía</li>
          <li><strong>Bicycle/Bike</strong> - Bicicleta</li>
          <li><strong>Motorcycle/Motorbike</strong> - Motocicleta</li>
        </ul>
      </div>
      
      <div class="air-transport">
        <h5>Air Transport:</h5>
        <ul>
          <li><strong>Plane</strong> - Avión</li>
          <li><strong>Helicopter</strong> - Helicóptero</li>
        </ul>
      </div>
      
      <div class="water-transport">
        <h5>Water Transport:</h5>
        <ul>
          <li><strong>Ship</strong> - Barco (large)</li>
          <li><strong>Boat</strong> - Bote/Barco (general)</li>
          <li><strong>Yacht</strong> - Yate (luxury boat)</li>
        </ul>
      </div>
    </div>

    <div class="transit-places">
      <h4>Transit Places (Lugares de Tránsito)</h4>
      <ul>
        <li><strong>Bus stop</strong> - Parada de bus</li>
        <li><strong>Train station</strong> - Estación de tren</li>
        <li><strong>Railroad tracks (US) / Railway line (UK)</strong> - Vías del tren</li>
        <li><strong>Road</strong> - Carretera</li>
        <li><strong>Taxi rank (UK) / Taxi stand (US)</strong> - Parada de taxis</li>
        <li><strong>Airport</strong> - Aeropuerto</li>
        <li><strong>Port</strong> - Puerto</li>
        <li><strong>Terminal</strong> - Terminal</li>
        <li><strong>Runway</strong> - Pista de aterrizaje</li>
      </ul>
    </div>

    <div class="collocations-critical">
      <h4>TRAVEL COLLOCATIONS (CRITICAL!) ⚠️</h4>
      <p><strong>Collocations = Natural verb + noun combinations that native speakers use!</strong></p>
      
      <div class="transport-collocations">
        <h5>Transportation Collocations:</h5>
        <ul>
          <li><strong>Drive</strong> a car (NOT "conduct" a car)</li>
          <li><strong>Ride</strong> a bike / Go cycling (NOT "drive" a bike)</li>
          <li><strong>Get on</strong> a bus/train (subir - NOT "enter")</li>
          <li><strong>Get off</strong> a bus/train (bajar - NOT "exit")</li>
          <li><strong>Board</strong> a plane (abordar - formal, NOT "enter")</li>
          <li><strong>Fly</strong> in a plane (NOT "go" in a plane)</li>
          <li><strong>Take</strong> a taxi/bus/train (usar transporte)</li>
        </ul>
      </div>
      
      <div class="airport-collocations">
        <h5>Airport Collocations:</h5>
        <ul>
          <li><strong>Arrive at</strong> the airport (NOT "arrive to")</li>
          <li><strong>Check in</strong> (at airport or hotel - registrarse)</li>
          <li><strong>Check out</strong> (at hotel - dejar el hotel)</li>
          <li><strong>Board</strong> a plane (abordar)</li>
          <li><strong>Catch/Miss</strong> a flight (agarrar/perder vuelo)</li>
        </ul>
      </div>
      
      <div class="journey-collocations">
        <h5>Journey Collocations:</h5>
        <ul>
          <li><strong>Pack</strong> your bags (NOT "make" your bags)</li>
          <li><strong>Set off</strong> on a journey (iniciar viaje - natural phrase)</li>
          <li><strong>Go on</strong> vacation/holiday</li>
          <li><strong>Be on time</strong> (a tiempo - NOT "in time" for schedules)</li>
        </ul>
      </div>
      
      <div class="hotel-collocations">
        <h5>Hotel Collocations:</h5>
        <ul>
          <li><strong>Arrive at</strong> a hotel (NOT "arrive to")</li>
          <li><strong>Check into</strong> a hotel (registrarse - NOT "check in at")</li>
          <li><strong>Stay in/at</strong> a hotel (quedarse)</li>
          <li><strong>Check out of</strong> a hotel (dejar hotel)</li>
          <li><strong>Leave</strong> a hotel (irse)</li>
        </ul>
      </div>
    </div>

    <div class="accommodation">
      <h4>Accommodation (Alojamiento)</h4>
      <ul>
        <li><strong>Hotel</strong> - Hotel (expensive, full service)</li>
        <li><strong>Hostel</strong> - Hostal (budget, shared rooms)</li>
        <li><strong>Reception</strong> - Recepción (front desk)</li>
      </ul>
    </div>

    <div class="travel-vocabulary">
      <h4>Other Travel Vocabulary</h4>
      <ul>
        <li><strong>Luggage / Baggage</strong> - Equipaje</li>
        <li><strong>Flight</strong> - Vuelo</li>
        <li><strong>Cruise</strong> - Crucero</li>
        <li><strong>Train ride</strong> - Viaje en tren</li>
        <li><strong>Vacation (US) / Holiday (UK)</strong> - Vacaciones</li>
        <li><strong>On time</strong> - A tiempo (puntual)</li>
      </ul>
    </div>
  `,

  iconType: "travel",

  sets: [
    // ========================================================================
    // SET 1: LAND TRANSPORTATION 🚗
    // ========================================================================
    {
      id: "m7-u12-s1",
      title: "Set 1: Land Transportation 🚗",
      context: `**Planning a Trip**

Friend: "How will you get around the city?"

You: "Let me think about the different land transport options..."`,
      
      questions: [
        {
          type: "match",
          q: "Match land vehicles with their descriptions:",
          pairs: [
            { left: "Car", right: "Personal 4-wheel vehicle" },
            { left: "Bus", right: "Large vehicle for many passengers (city)" },
            { left: "Train", right: "Runs on railroad tracks" },
            { left: "Bicycle", right: "Two-wheel pedal vehicle" }
          ],
          successtip: "✓ LAND TRANSPORT!\nEach vehicle serves different purposes! Foundation vocabulary! 🚗🚌"
        },
        {
          type: "mc",
          q: "You: 'I'll drive my ___ to work.' (personal vehicle)",
          options: [
            "car",
            "plane",
            "ship"
          ],
          a: "car",
          successtip: "✓ CAR!\nCar = carro/coche! Personal vehicle with 4 wheels! Most common transport! 🚗"
        },
        {
          type: "blank",
          q: "Friend: 'You can call a ___ to get there quickly.' (hired car with driver)",
          a: "taxi",
          accept: { ignoreCase: true },
          successtip: "✓ TAXI!\nTaxi = hired car with driver! Pay for ride! Quick city transport! 🚕"
        },
        {
          type: "mc",
          q: "You: 'I'll take the ___ - it's cheap and goes downtown.' (large city vehicle)",
          options: [
            "bus",
            "yacht",
            "helicopter"
          ],
          a: "bus",
          successtip: "✓ BUS!\nBus = autobús! Large vehicle, many passengers! Stops frequently in city! 🚌"
        },
        {
          type: "order",
          q: "Traduce: El tren es rápido y cómodo para viajes largos",
          a: "The train is fast and comfortable for long trips",
          successtip: "✓ TRAIN!\nTrain = tren! Runs on tracks! Long-distance and commuter trains! 🚆"
        },
        {
          type: "mc",
          q: "Friend: 'For long-distance travel, take a ___.' (long-distance bus)",
          options: [
            "coach",
            "tram",
            "bicycle"
          ],
          a: "coach",
          successtip: "✓ COACH!\nCoach = autocar! Long-distance bus with comfortable seats! UK term especially! 🚌"
        },
        {
          type: "blank",
          q: "You: 'The city has a ___ that runs on tracks in the street.' (city rail vehicle)",
          a: "tram",
          accept: { ignoreCase: true },
          successtip: "✓ TRAM!\nTram = tranvía! Electric vehicle on rails in city streets! Common in Europe! 🚋"
        },
        {
          type: "mc",
          q: "You: 'I'll ride my ___ - it's good exercise!' (two-wheel pedal vehicle)",
          options: [
            "bicycle / bike",
            "motorcycle",
            "car"
          ],
          a: "bicycle / bike",
          successtip: "✓ BICYCLE!\nBicycle/Bike = bicicleta! Two wheels, pedal power! Eco-friendly! 🚴"
        },
        {
          type: "order",
          q: "Traduce: Las motocicletas son rápidas pero peligrosas",
          a: "Motorcycles are fast but dangerous",
          successtip: "✓ MOTORCYCLE!\nMotorcycle/Motorbike = motocicleta/moto! Two wheels with engine! Fast! 🏍️"
        },
        {
          type: "mc",
          q: "Summary: Land transport includes:",
          options: [
            "Cars, buses, trains, trams, bikes, motorcycles (all on land)",
            "Only airplanes",
            "Only boats"
          ],
          a: "Cars, buses, trains, trams, bikes, motorcycles (all on land)",
          successtip: "✓ LAND VEHICLES!\nAll these move on roads or tracks! No flying or water! Next: air & water! 🚗🚌🚆"
        }
      ]
    },

    // ========================================================================
    // SET 2: AIR & WATER TRANSPORTATION ✈️🚢
    // ========================================================================
    {
      id: "m7-u12-s2",
      title: "Set 2: Air & Water Transportation ✈️🚢",
      context: `**Vacation Planning**

Travel agent: "For your island vacation, you'll need air and water transport!"

You: "Tell me about the options!"`,
      
      questions: [
        {
          type: "match",
          q: "Match air and water vehicles with their uses:",
          pairs: [
            { left: "Plane", right: "Air travel, long distances" },
            { left: "Helicopter", right: "Air travel, vertical takeoff" },
            { left: "Ship", right: "Large water vessel" },
            { left: "Boat", right: "Small to medium water vessel" }
          ],
          successtip: "✓ AIR & WATER!\nDifferent environments require different vehicles! ✈️🚢"
        },
        {
          type: "mc",
          q: "Agent: 'You'll fly to the island on a ___.' (air vehicle, large)",
          options: [
            "plane",
            "train",
            "bus"
          ],
          a: "plane",
          successtip: "✓ PLANE!\nPlane = avión! Flies through air! For long distances! ✈️"
        },
        {
          type: "blank",
          q: "Agent: 'For a scenic view, you can take a ___.' (air vehicle, vertical)",
          a: "helicopter",
          accept: { ignoreCase: true },
          successtip: "✓ HELICOPTER!\nHelicopter = helicóptero! Vertical takeoff/landing! Rotors on top! 🚁"
        },
        {
          type: "mc",
          q: "Agent: 'The cruise will be on a large ___.' (large water vessel)",
          options: [
            "ship",
            "plane",
            "car"
          ],
          a: "ship",
          successtip: "✓ SHIP!\nShip = barco (grande)! Large water vessel! Cruise ships, cargo ships! 🚢"
        },
        {
          type: "order",
          q: "Traduce: Alquilamos un bote para pescar",
          a: "We rented a boat to go fishing",
          successtip: "✓ BOAT!\nBoat = bote/barco! General term, smaller than ship! Fishing boats, row boats! ⛵"
        },
        {
          type: "mc",
          q: "Agent: 'A luxury ___ is perfect for sailing!' (luxury private boat)",
          options: [
            "yacht",
            "bus",
            "bicycle"
          ],
          a: "yacht",
          successtip: "✓ YACHT!\nYacht = yate! Luxury boat for sailing or cruising! Expensive! ⛵💰"
        },
        {
          type: "blank",
          q: "You: 'The ___ takes off from the runway.' (which air vehicle?)",
          a: "plane",
          accept: { ignoreCase: true, alts: ["airplane"] },
          successtip: "✓ PLANE TAKEOFF!\nPlanes need runways! Helicopters don't! Key difference! ✈️"
        },
        {
          type: "mc",
          q: "Complete: 'We'll take a ___ ride across the lake.' (water transport)",
          options: [
            "boat",
            "plane",
            "train"
          ],
          a: "boat",
          successtip: "✓ BOAT RIDE!\nBoat = perfect for lakes, rivers, short water trips! ⛵"
        },
        {
          type: "order",
          q: "Traduce: El helicóptero aterrizó en el techo del edificio",
          a: "The helicopter landed on the building roof",
          successtip: "✓ HELICOPTER ADVANTAGE!\nHelicopters = land anywhere! Roofs, small spaces! No runway needed! 🚁"
        },
        {
          type: "mc",
          q: "Summary: Transportation types are:",
          options: [
            "Land (car, bus, train), Air (plane, helicopter), Water (ship, boat, yacht)",
            "Only land vehicles",
            "All the same"
          ],
          a: "Land (car, bus, train), Air (plane, helicopter), Water (ship, boat, yacht)",
          successtip: "✓ THREE CATEGORIES!\nLand, Air, Water = all transport covered! 13 vehicles total! 🚗✈️🚢"
        }
      ]
    },

    // ========================================================================
    // SET 3: TRANSIT PLACES 🚉
    // ========================================================================
    {
      id: "m7-u12-s3",
      title: "Set 3: Transit Places & Infrastructure 🚉",
      context: `**City Navigation**

Tourist: "Where do I catch different types of transport?"

Local: "Let me show you all the transit places..."`,
      
      questions: [
        {
          type: "match",
          q: "Match transit places with their purposes:",
          pairs: [
            { left: "Bus stop", right: "Where buses pick up passengers" },
            { left: "Train station", right: "Where trains arrive and depart" },
            { left: "Airport", right: "Where planes take off and land" },
            { left: "Port", right: "Where ships dock" }
          ],
          successtip: "✓ TRANSIT PLACES!\nEach transport type has its specific location! 🚏🚉✈️"
        },
        {
          type: "mc",
          q: "Local: 'Wait at the ___ for the bus.' (bus waiting place)",
          options: [
            "bus stop",
            "airport",
            "port"
          ],
          a: "bus stop",
          successtip: "✓ BUS STOP!\nBus stop = parada de bus! Small sign/shelter on street! 🚏"
        },
        {
          type: "blank",
          q: "Local: 'Catch your train at the ___ ___.' (large building for trains)",
          a: "train station",
          accept: { ignoreCase: true },
          successtip: "✓ TRAIN STATION!\nTrain station = estación de tren! Large building, platforms, schedules! 🚉"
        },
        {
          type: "mc",
          q: "Tourist: 'Where do trains run?' Local: 'On the ___' (metal tracks)",
          options: [
            "railroad tracks / railway line",
            "road",
            "runway"
          ],
          a: "railroad tracks / railway line",
          successtip: "✓ TRAIN TRACKS!\nUS: railroad tracks. UK: railway line. Metal rails for trains! 🛤️"
        },
        {
          type: "order",
          q: "Traduce: Los carros conducen en la carretera",
          a: "Cars drive on the road",
          successtip: "✓ ROAD!\nRoad = carretera/camino! Paved surface for vehicles! 🛣️"
        },
        {
          type: "mc",
          q: "Local: 'Find a taxi at the ___.' (UK: rank, US: stand)",
          options: [
            "taxi rank / taxi stand",
            "train station",
            "runway"
          ],
          a: "taxi rank / taxi stand",
          successtip: "✓ TAXI LOCATION!\nUK: taxi rank. US: taxi stand. Place where taxis wait! 🚕🚏"
        },
        {
          type: "blank",
          q: "Tourist: 'Where do I catch my flight?' Local: 'At the ___'",
          a: "airport",
          accept: { ignoreCase: true },
          successtip: "✓ AIRPORT!\nAirport = aeropuerto! Where planes take off and land! Terminals, runways! ✈️"
        },
        {
          type: "mc",
          q: "Local: 'The plane takes off from the ___.' (long paved strip)",
          options: [
            "runway",
            "road",
            "railway"
          ],
          a: "runway",
          successtip: "✓ RUNWAY!\nRunway = pista de aterrizaje! Long strip where planes take off and land! 🛫"
        },
        {
          type: "order",
          q: "Traduce: El barco está en el puerto",
          a: "The ship is at the port",
          successtip: "✓ PORT!\nPort = puerto! Where ships dock, load/unload! Harbor! ⚓🚢"
        },
        {
          type: "mc",
          q: "Local: 'Your flight leaves from ___ 3.' (airport section)",
          options: [
            "terminal",
            "port",
            "bus stop"
          ],
          a: "terminal",
          successtip: "✓ TERMINAL!\nTerminal = building at airport or bus station! Different terminals for different flights/routes! 🏢"
        }
      ]
    },

    // ========================================================================
    // SET 4: TRANSPORT COLLOCATIONS 🚌
    // ========================================================================
    {
      id: "m7-u12-s4",
      title: "Set 4: Transportation Collocations 🚌",
      context: `**Learning Natural English**

Teacher: "Don't translate literally! Use these natural verb + noun combinations..."

Student: "Show me the correct collocations!"`,
      
      questions: [
        {
          type: "mc",
          q: "Teacher: 'With cars, we say: ___ a car' (NOT 'conduct')",
          options: [
            "drive",
            "ride",
            "board"
          ],
          a: "drive",
          successtip: "✓ DRIVE A CAR!\n'Drive a car' = conducir! NOT 'conduct'! Natural collocation! 🚗"
        },
        {
          type: "blank",
          q: "Teacher: 'With bikes, we say: ___ a bike' (NOT 'drive')",
          a: "ride",
          accept: { ignoreCase: true },
          successtip: "✓ RIDE A BIKE!\n'Ride a bike' = andar en bici! Or 'go cycling'! NOT 'drive a bike'! 🚴"
        },
        {
          type: "mc",
          q: "Teacher: 'To enter a bus or train, we say: ___' (subir)",
          options: [
            "get on",
            "enter",
            "board"
          ],
          a: "get on",
          successtip: "✓ GET ON!\n'Get on' a bus/train = subir! More natural than 'enter'! 🚌⬆️"
        },
        {
          type: "order",
          q: "Traduce usando collocación natural: Bájate del autobús en la próxima parada",
          a: "Get off the bus at the next stop",
          successtip: "✓ GET OFF!\n'Get off' = bajar del bus/tren! Opposite of 'get on'! Natural collocation! 🚌⬇️"
        },
        {
          type: "mc",
          q: "Teacher: 'For planes, the formal word for entering is: ___' (NOT 'get on')",
          options: [
            "board",
            "enter",
            "ride"
          ],
          a: "board",
          successtip: "✓ BOARD A PLANE!\n'Board' = abordar! Formal for planes and ships! NOT 'get on a plane'! ✈️"
        },
        {
          type: "blank",
          q: "Complete naturally: '___ a taxi' (usar/tomar un taxi)",
          a: "take",
          accept: { ignoreCase: true },
          successtip: "✓ TAKE A TAXI!\n'Take a taxi/bus/train' = common collocation! Use transport! 🚕"
        },
        {
          type: "mc",
          q: "Complete: 'We will ___ in a plane to Paris.' (volar)",
          options: [
            "fly",
            "go",
            "walk"
          ],
          a: "fly",
          successtip: "✓ FLY IN A PLANE!\n'Fly in a plane' = natural! NOT just 'go in a plane'! ✈️"
        },
        {
          type: "order",
          q: "Error: 'I conduct my car' → Correct: 'I ___ my car'",
          a: "drive",
          successtip: "✓ DRIVE NOT CONDUCT!\nCommon error! 'Drive' a car, NOT 'conduct'! Spanish 'conducir' ≠ English 'conduct'! 🚗"
        },
        {
          type: "mc",
          q: "Complete naturally: 'Let's ___ cycling this weekend!' (activity)",
          options: [
            "go",
            "do",
            "make"
          ],
          a: "go",
          successtip: "✓ GO CYCLING!\n'Go cycling' = andar en bici (activity)! Alternative to 'ride a bike'! 🚴"
        },
        {
          type: "mc",
          q: "CRITICAL: Why learn collocations?",
          options: [
            "Native speakers use specific verb + noun pairs - sounds natural!",
            "Direct translation is always correct",
            "Any verb works with any noun"
          ],
          a: "Native speakers use specific verb + noun pairs - sounds natural!",
          successtip: "✓ COLLOCATIONS = NATURAL!\nDrive a car, ride a bike, board a plane = fixed combinations! Sound like a native! 🎯"
        }
      ]
    },

    // ========================================================================
    // SET 5: AIRPORT COLLOCATIONS ✈️
    // ========================================================================
    {
      id: "m7-u12-s5",
      title: "Set 5: Airport Collocations ✈️",
      context: `**First Time Flying**

Experienced traveler: "Let me teach you the natural phrases for airports..."

Nervous flyer: "I want to sound natural!"`,
      
      questions: [
        {
          type: "match",
          q: "Match airport collocations with their meanings:",
          pairs: [
            { left: "Arrive at the airport", right: "Llegar al aeropuerto" },
            { left: "Check in", right: "Registrarse (dar equipaje)" },
            { left: "Board a plane", right: "Abordar el avión" },
            { left: "Catch a flight", right: "Agarrar/tomar un vuelo" }
          ],
          successtip: "✓ AIRPORT PHRASES!\nSpecific combinations for airport process! Natural English! ✈️"
        },
        {
          type: "mc",
          q: "Traveler: 'We say: ___ the airport' (NOT 'arrive to')",
          options: [
            "arrive AT",
            "arrive TO",
            "arrive IN"
          ],
          a: "arrive AT",
          successtip: "✓ ARRIVE AT!\n'Arrive AT' the airport! NOT 'arrive to'! Preposition matters! ✈️📍"
        },
        {
          type: "blank",
          q: "Traveler: 'After arriving, you ___ ___.' (registrarse, dar equipaje - 2 words)",
          a: "check in",
          accept: { ignoreCase: true },
          successtip: "✓ CHECK IN!\n'Check in' = registrarse at airport! Give luggage, get boarding pass! ✅"
        },
        {
          type: "mc",
          q: "Traveler: 'When it's time to enter the plane, you ___ it.' (abordar)",
          options: [
            "board",
            "enter",
            "get on"
          ],
          a: "board",
          successtip: "✓ BOARD A PLANE!\n'Board' = abordar! Formal airport term! NOT 'get on' for planes! ✈️"
        },
        {
          type: "order",
          q: "Traduce con collocation: Perdí mi vuelo porque llegué tarde",
          a: "I missed my flight because I arrived late",
          successtip: "✓ MISS A FLIGHT!\n'Miss a flight' = perder un vuelo! Opposite: 'catch a flight'! ✈️❌"
        },
        {
          type: "mc",
          q: "Traveler: 'If you're on time, you'll ___ your flight.' (agarrar)",
          options: [
            "catch",
            "take",
            "get"
          ],
          a: "catch",
          successtip: "✓ CATCH A FLIGHT!\n'Catch a flight' = agarrar/tomar vuelo! Make it in time! ✈️✅"
        },
        {
          type: "blank",
          q: "Complete: 'My ___ leaves at 3 PM.' (vuelo)",
          a: "flight",
          accept: { ignoreCase: true },
          successtip: "✓ FLIGHT!\nFlight = vuelo! One trip by plane! 'Flight number', 'international flight'! ✈️"
        },
        {
          type: "mc",
          q: "Airport sequence: arrive → check in → ___ → fly",
          options: [
            "board",
            "drive",
            "ride"
          ],
          a: "board",
          successtip: "✓ AIRPORT PROCESS!\nArrive AT airport → Check IN → Board plane → Fly! Natural sequence! ✈️"
        },
        {
          type: "order",
          q: "Traduce: Tengo que facturar mi equipaje",
          a: "I have to check in my luggage",
          successtip: "✓ CHECK IN LUGGAGE!\nCheck in = facturar! Give bags to airline! Receive ticket! ✈️🧳"
        },
        {
          type: "mc",
          q: "What's the boarding pass?",
          options: [
            "Ticket to board the plane (after checking in)",
            "Passport",
            "Luggage tag"
          ],
          a: "Ticket to board the plane (after checking in)",
          successtip: "✓ BOARDING PASS!\nBoarding pass = pase de abordar! Receive at check-in! Need it to board! 🎫✈️"
        }
      ]
    },

    // ========================================================================
    // SET 6: JOURNEY COLLOCATIONS 🧳
    // ========================================================================
    {
      id: "m7-u12-s6",
      title: "Set 6: Journey & Travel Collocations 🧳",
      context: `**Travel Preparation**

Friend: "What do you do before and during a journey?"

You: "There are specific phrases for each stage..."`,
      
      questions: [
        {
          type: "match",
          q: "Match travel collocations with meanings:",
          pairs: [
            { left: "Pack your bags", right: "Empacar maletas" },
            { left: "Set off on a journey", right: "Iniciar un viaje" },
            { left: "Be on time", right: "Ser puntual" },
            { left: "Go on vacation", right: "Ir de vacaciones" }
          ],
          successtip: "✓ JOURNEY PHRASES!\nNatural expressions for travel stages! 🧳✈️"
        },
        {
          type: "mc",
          q: "Friend: 'Before traveling, you must ___ your bags.' (NOT 'make')",
          options: [
            "pack",
            "make",
            "do"
          ],
          a: "pack",
          successtip: "✓ PACK YOUR BAGS!\n'Pack bags' = empacar maletas! NOT 'make bags'! Natural collocation! 🧳"
        },
        {
          type: "blank",
          q: "Friend: 'All your bags and suitcases = your ___.' (equipaje)",
          a: "luggage",
          accept: { ignoreCase: true, alts: ["baggage"] },
          successtip: "✓ LUGGAGE!\nLuggage/Baggage = equipaje! All bags together! Uncountable noun! 🧳"
        },
        {
          type: "mc",
          q: "Friend: 'To begin a trip, we ___ on a journey.' (natural phrase)",
          options: [
            "set off",
            "start",
            "begin"
          ],
          a: "set off",
          successtip: "✓ SET OFF!\n'Set off on a journey' = iniciar viaje! More natural than just 'start'! 🚀"
        },
        {
          type: "order",
          q: "Traduce: Salimos temprano para estar a tiempo",
          a: "We left early to be on time",
          successtip: "✓ BE ON TIME!\n'Be on time' = ser puntual! Arrive at scheduled time! NOT 'in time'! ⏰✅"
        },
        {
          type: "mc",
          q: "Friend: 'US: vacation, UK: ___' (vacaciones)",
          options: [
            "holiday",
            "vacation",
            "day off"
          ],
          a: "holiday",
          successtip: "✓ HOLIDAY vs VACATION!\nUS: vacation. UK: holiday. Same meaning! 'Go on vacation/holiday'! 🏖️🇺🇸🇬🇧"
        },
        {
          type: "blank",
          q: "Complete: 'We went on a ___ ___.' (viaje en tren - specific phrase)",
          a: "train ride",
          accept: { ignoreCase: true },
          successtip: "✓ TRAIN RIDE!\n'Train ride' = viaje en tren! 'Boat ride', 'bus ride' = same pattern! 🚆"
        },
        {
          type: "mc",
          q: "Friend: 'A luxury boat trip is called a ___.'",
          options: [
            "cruise",
            "ride",
            "flight"
          ],
          a: "cruise",
          successtip: "✓ CRUISE!\nCruise = crucero! Luxury ship vacation! Days/weeks on water! 🚢⚓"
        },
        {
          type: "order",
          q: "Traduce: Vamos de vacaciones a España el próximo mes",
          a: "We are going on vacation to Spain next month",
          successtip: "✓ GO ON VACATION!\n'Go on vacation/holiday' = ir de vacaciones! Natural collocation! 🏖️✈️"
        },
        {
          type: "mc",
          q: "Difference: 'on time' vs 'in time':",
          options: [
            "'On time' = punctual (10:00 sharp). 'In time' = before deadline (before too late)",
            "They mean exactly the same",
            "'In time' = punctual"
          ],
          a: "'On time' = punctual (10:00 sharp). 'In time' = before deadline (before too late)",
          successtip: "✓ ON TIME vs IN TIME!\nOn time = puntual/exacto. In time = antes del límite. For schedules: 'on time'! ⏰"
        }
      ]
    },

    // ========================================================================
    // SET 7: HOTEL COLLOCATIONS 🏨
    // ========================================================================
    {
      id: "m7-u12-s7",
      title: "Set 7: Hotel & Accommodation Collocations 🏨",
      context: `**Hotel Stay**

Receptionist: "Welcome! Let me explain our hotel procedures..."

Guest: "What are the natural phrases I should use?"`,
      
      questions: [
        {
          type: "match",
          q: "Match hotel collocations with meanings:",
          pairs: [
            { left: "Arrive at a hotel", right: "Llegar al hotel" },
            { left: "Check into a hotel", right: "Registrarse (entrar)" },
            { left: "Stay in/at a hotel", right: "Quedarse en hotel" },
            { left: "Check out of a hotel", right: "Dejar el hotel (salir)" }
          ],
          successtip: "✓ HOTEL PHRASES!\nSpecific collocations for hotel process! Natural English! 🏨"
        },
        {
          type: "mc",
          q: "Receptionist: 'We say: ___ the hotel' (NOT 'arrive to')",
          options: [
            "arrive AT",
            "arrive TO",
            "arrive IN"
          ],
          a: "arrive AT",
          successtip: "✓ ARRIVE AT!\n'Arrive AT' a hotel! Same as airport! NOT 'arrive to'! 🏨📍"
        },
        {
          type: "blank",
          q: "Receptionist: 'When you arrive, you ___ ___' (registrarse - 2 words + preposition)",
          a: "check into",
          accept: { ignoreCase: true, alts: ["check in to", "check in"] },
          successtip: "✓ CHECK INTO!\n'Check into a hotel' = registrarse, dar info! Get room key! Note: 'into' not 'in to'! 🏨✅"
        },
        {
          type: "mc",
          q: "Guest: 'Where do I check in?' Receptionist: 'At the ___' (front desk)",
          options: [
            "reception",
            "runway",
            "platform"
          ],
          a: "reception",
          successtip: "✓ RECEPTION!\nReception = recepción/front desk! Where you check in/out! 🏨📋"
        },
        {
          type: "order",
          q: "Traduce: Nos quedamos en un hotel de cinco estrellas",
          a: "We stayed in a five-star hotel",
          successtip: "✓ STAY IN A HOTEL!\n'Stay in/at a hotel' = quedarse! Both 'in' and 'at' work! 🏨🌟"
        },
        {
          type: "mc",
          q: "Guest: 'When leaving, you ___ of the hotel.' (dejar hotel)",
          options: [
            "check out",
            "check in",
            "board"
          ],
          a: "check out",
          successtip: "✓ CHECK OUT!\n'Check out' = dejar el hotel, pagar cuenta final! Opposite of check in! 🏨⬅️"
        },
        {
          type: "blank",
          q: "Receptionist: 'After checking out, you ___ the hotel.' (irse)",
          a: "leave",
          accept: { ignoreCase: true },
          successtip: "✓ LEAVE A HOTEL!\n'Leave' = irse! Simple verb, natural! Check out → leave! 🏨👋"
        },
        {
          type: "mc",
          q: "Difference: Hotel vs Hostel:",
          options: [
            "Hotel = expensive, private rooms. Hostel = budget, often shared rooms",
            "They're the same",
            "Hostel = more expensive"
          ],
          a: "Hotel = expensive, private rooms. Hostel = budget, often shared rooms",
          successtip: "✓ HOTEL vs HOSTEL!\nHotel = caro, privado. Hostel = económico, compartido! Backpackers love hostels! 🏨💰 vs 🏠💵"
        },
        {
          type: "order",
          q: "Hotel process: Arrive at → ___ → Stay in → ___ → Leave",
          a: "Check into, Check out of",
          successtip: "✓ HOTEL SEQUENCE!\nArrive AT → Check INTO → Stay IN → Check OUT OF → Leave! Complete process! 🏨"
        },
        {
          type: "mc",
          q: "Complete naturally: 'I'll ___ at a hotel near the airport.'",
          options: [
            "stay",
            "sit",
            "board"
          ],
          a: "stay",
          successtip: "✓ STAY AT A HOTEL!\n'Stay at/in a hotel' = quedarse! Natural collocation! 🏨😴"
        }
      ]
    },

    // ========================================================================
    // SET 8: VACATION & TRAVEL VOCABULARY 🏖️
    // ========================================================================
    {
      id: "m7-u12-s8",
      title: "Set 8: Vacation & Travel Vocabulary 🏖️",
      context: `**Planning Your Dream Trip**

Travel blogger: "Let me share essential travel vocabulary..."

Follower: "I want to plan the perfect vacation!"`,
      
      questions: [
        {
          type: "mc",
          q: "Blogger: 'US: vacation, UK: ___' (time off for travel)",
          options: [
            "holiday",
            "day off",
            "weekend"
          ],
          a: "holiday",
          successtip: "✓ VACATION vs HOLIDAY!\nUS: vacation. UK: holiday. Both = vacaciones! 'Summer vacation/holiday'! 🏖️🇺🇸🇬🇧"
        },
        {
          type: "blank",
          q: "Blogger: 'All your bags and suitcases together = ___' (uncountable)",
          a: "luggage",
          accept: { ignoreCase: true, alts: ["baggage"] },
          successtip: "✓ LUGGAGE!\nLuggage = equipaje! Uncountable! NOT 'luggages'! One suitcase or ten = 'luggage'! 🧳"
        },
        {
          type: "mc",
          q: "Blogger: 'A trip by plane from one place to another = ___'",
          options: [
            "flight",
            "ride",
            "drive"
          ],
          a: "flight",
          successtip: "✓ FLIGHT!\nFlight = vuelo! One journey by plane! 'Direct flight', 'connecting flight'! ✈️"
        },
        {
          type: "order",
          q: "Traduce: Fuimos en un crucero por el Caribe",
          a: "We went on a cruise around the Caribbean",
          successtip: "✓ CRUISE!\nCruise = crucero! Multi-day boat vacation! Caribbean cruises = popular! 🚢🏝️"
        },
        {
          type: "mc",
          q: "Blogger: 'A journey specifically by train = ___'",
          options: [
            "train ride",
            "train flight",
            "train drive"
          ],
          a: "train ride",
          successtip: "✓ TRAIN RIDE!\n'Train ride' = viaje en tren! Pattern: 'boat ride', 'bike ride', 'car ride'! 🚆"
        },
        {
          type: "blank",
          q: "Blogger: 'Being punctual, arriving at scheduled time = be ___ ___'",
          a: "on time",
          accept: { ignoreCase: true },
          successtip: "✓ ON TIME!\n'On time' = puntual! Arrive at exact scheduled time! Critical for travel! ⏰✅"
        },
        {
          type: "mc",
          q: "Complete naturally: 'Let's ___ on vacation to Italy!'",
          options: [
            "go",
            "make",
            "do"
          ],
          a: "go",
          successtip: "✓ GO ON VACATION!\n'Go on vacation/holiday' = natural collocation! NOT 'make vacation'! 🏖️✈️"
        },
        {
          type: "order",
          q: "Traduce: Empaqué mis maletas la noche anterior",
          a: "I packed my bags the night before",
          successtip: "✓ PACK BAGS!\n'Pack bags' = empacar maletas! Before every trip! NOT 'make bags'! 🧳"
        },
        {
          type: "mc",
          q: "Blogger: 'To begin your journey, you ___ on a trip.' (natural phrase)",
          options: [
            "set off",
            "do",
            "make"
          ],
          a: "set off",
          successtip: "✓ SET OFF!\n'Set off' = iniciar viaje! More natural than 'start'! 'We set off at dawn'! 🚀🌅"
        },
        {
          type: "mc",
          q: "Which is budget accommodation?",
          options: [
            "Hostel (shared rooms, cheap)",
            "Five-star hotel",
            "Luxury resort"
          ],
          a: "Hostel (shared rooms, cheap)",
          successtip: "✓ HOSTEL!\nHostel = hostal! Budget option, often shared rooms, backpacker favorite! 🏠💵"
        }
      ]
    },

    // ========================================================================
    // SET 9: COMPLETE TRAVEL JOURNEY 🌍
    // ========================================================================
    {
      id: "m7-u12-s9",
      title: "Set 9: Complete Travel Journey Integration 🌍",
      context: `**FINAL INTEGRATION**

Following a complete journey from preparation to arrival...`,
      
      questions: [
        {
          type: "mc",
          q: "Step 1: Before traveling, you ___ your bags.",
          options: [
            "pack",
            "make",
            "do"
          ],
          a: "pack",
          successtip: "✓ STEP 1!\nPack bags = first step! Preparation before journey! 🧳"
        },
        {
          type: "blank",
          q: "Step 2: You ___ ___ on your journey early in the morning. (begin trip)",
          a: "set off",
          accept: { ignoreCase: true },
          successtip: "✓ STEP 2!\nSet off = begin journey! Leave home for destination! 🚀"
        },
        {
          type: "mc",
          q: "Step 3: You take a ___ to get to the airport. (hired car)",
          options: [
            "taxi",
            "plane",
            "boat"
          ],
          a: "taxi",
          successtip: "✓ STEP 3!\nTake a taxi to airport! Quick transport with luggage! 🚕"
        },
        {
          type: "order",
          q: "Step 4: Traduce - Llegas al aeropuerto a tiempo",
          a: "You arrive at the airport on time",
          successtip: "✓ STEP 4!\nArrive AT airport + on time! Two collocations! ✈️⏰"
        },
        {
          type: "mc",
          q: "Step 5: At the airport, you ___ your luggage.",
          options: [
            "check in",
            "pack",
            "drive"
          ],
          a: "check in",
          successtip: "✓ STEP 5!\nCheck in luggage = facturar! Give bags to airline! 🧳✅"
        },
        {
          type: "blank",
          q: "Step 6: You ___ the plane when it's time. (abordar)",
          a: "board",
          accept: { ignoreCase: true },
          successtip: "✓ STEP 6!\nBoard the plane = abordar! Enter aircraft! ✈️"
        },
        {
          type: "mc",
          q: "Step 7: You ___ in a plane to your destination.",
          options: [
            "fly",
            "drive",
            "walk"
          ],
          a: "fly",
          successtip: "✓ STEP 7!\nFly in a plane = natural collocation! The journey! ✈️☁️"
        },
        {
          type: "order",
          q: "Step 8: Traduce - Llegas a tu hotel",
          a: "You arrive at your hotel",
          successtip: "✓ STEP 8!\nArrive AT hotel! Same preposition as airport! 🏨"
        },
        {
          type: "mc",
          q: "Step 9: At the hotel, you ___ and get your room key.",
          options: [
            "check in / check into the hotel",
            "board",
            "pack"
          ],
          a: "check in / check into the hotel",
          successtip: "✓ STEP 9!\nCheck in(to) hotel = registrarse! Get room! 🏨🔑"
        },
        {
          type: "mc",
          q: "Step 10: You ___ at the hotel for three days. (quedarse)",
          options: [
            "stay",
            "board",
            "drive"
          ],
          a: "stay",
          successtip: "✓ COMPLETE JOURNEY!\nStay = quedarse! Full journey complete: Pack → Set off → Taxi → Airport → Check in → Board → Fly → Hotel → Check in → Stay! 🌍✈️🏨"
        }
      ]
    },

    // ========================================================================
    // SET 10: COLLOCATIONS MASTERY 🎯
    // ========================================================================
    {
      id: "m7-u12-s10",
      title: "Set 10: All Collocations Mastery 🎯",
      context: `**FINAL CHALLENGE**

Master ALL travel collocations naturally!`,
      
      questions: [
        {
          type: "mc",
          q: "Complete: '___ a car' (conducir)",
          options: [
            "Drive",
            "Ride",
            "Conduct"
          ],
          a: "Drive",
          successtip: "✓ DRIVE A CAR!\nDrive = conducir! NOT 'conduct'! 🚗"
        },
        {
          type: "blank",
          q: "Complete: '___ a bike' (andar en bici)",
          a: "Ride",
          accept: { ignoreCase: true },
          successtip: "✓ RIDE A BIKE!\nRide a bike! NOT 'drive a bike'! 🚴"
        },
        {
          type: "mc",
          q: "Complete: '___ on a bus' (subir)",
          options: [
            "Get",
            "Board",
            "Enter"
          ],
          a: "Get",
          successtip: "✓ GET ON!\n'Get on' a bus/train! Subir! 🚌⬆️"
        },
        {
          type: "order",
          q: "Complete: '___ off a bus' (bajar)",
          a: "Get",
          successtip: "✓ GET OFF!\n'Get off' = bajar! Opposite of 'get on'! 🚌⬇️"
        },
        {
          type: "mc",
          q: "Complete: '___ a plane' (abordar - formal)",
          options: [
            "Board",
            "Get on",
            "Enter"
          ],
          a: "Board",
          successtip: "✓ BOARD A PLANE!\nBoard = abordar! Formal for planes/ships! ✈️"
        },
        {
          type: "blank",
          q: "Complete: 'Arrive ___ the airport' (preposition)",
          a: "at",
          accept: { ignoreCase: true },
          successtip: "✓ ARRIVE AT!\nArrive AT (not TO) airport/hotel/station! 📍"
        },
        {
          type: "mc",
          q: "Complete: '___ in your luggage' (at airport)",
          options: [
            "Check",
            "Pack",
            "Board"
          ],
          a: "Check",
          successtip: "✓ CHECK IN!\nCheck in luggage = facturar! 🧳✅"
        },
        {
          type: "order",
          q: "Complete: '___ your bags' (before trip)",
          a: "Pack",
          successtip: "✓ PACK BAGS!\nPack (not 'make') your bags! 🧳"
        },
        {
          type: "mc",
          q: "Complete: '___ off on a journey' (begin)",
          options: [
            "Set",
            "Start",
            "Make"
          ],
          a: "Set",
          successtip: "✓ SET OFF!\n'Set off' = natural phrase for beginning journey! 🚀"
        },
        {
          type: "mc",
          q: "FINAL MASTERY: Which shows COMPLETE Unit 12 knowledge?",
          options: [
            "'I packed my bags, set off early, drove my car to the airport, arrived on time, checked in my luggage, boarded the plane, flew to Paris, arrived at my hotel, checked in, and stayed for a week!'",
            "'I made my bags and conducted my car to airport. I entered the plane and stayed hotel.'",
            "'I go airport with car. I travel plane. I hotel.'"
          ],
          a: "'I packed my bags, set off early, drove my car to the airport, arrived on time, checked in my luggage, boarded the plane, flew to Paris, arrived at my hotel, checked in, and stayed for a week!'",
          successtip: "🎉 UNIT 12 COMPLETELY MASTERED! 🎉\n\n✅ TRANSPORTATION (13 vehicles!)\n• LAND: Car, taxi, bus, coach, train, tram, bike, motorcycle ✅\n• AIR: Plane, helicopter ✅\n• WATER: Ship, boat, yacht ✅\n\n✅ TRANSIT PLACES\n• Bus stop, train station, railway tracks ✅\n• Road, taxi rank, airport ✅\n• Port, terminal, runway ✅\n\n✅ TRANSPORT COLLOCATIONS\n• Drive a car (NOT conduct!) ✅\n• Ride a bike (NOT drive!) ✅\n• Get on/off a bus (subir/bajar) ✅\n• Board a plane (abordar) ✅\n• Fly in a plane ✅\n• Take a taxi/bus/train ✅\n\n✅ AIRPORT COLLOCATIONS\n• Arrive AT the airport (NOT to!) ✅\n• Check in (luggage) ✅\n• Board a plane ✅\n• Catch/Miss a flight ✅\n• Flight (vuelo) ✅\n\n✅ JOURNEY COLLOCATIONS\n• Pack your bags (NOT make!) ✅\n• Set off on a journey ✅\n• Be on time (puntual) ✅\n• Go on vacation/holiday ✅\n• Train ride, boat ride ✅\n• Luggage (equipaje) ✅\n• Cruise (crucero) ✅\n\n✅ HOTEL COLLOCATIONS\n• Arrive AT a hotel (NOT to!) ✅\n• Check into a hotel ✅\n• Stay in/at a hotel ✅\n• Check out of a hotel ✅\n• Leave a hotel ✅\n• Reception (recepción) ✅\n\n✅ ACCOMMODATION\n• Hotel (expensive, private) ✅\n• Hostel (budget, shared) ✅\n\n✅ CORRECT IN FINAL SENTENCE:\n• Packed bags (not 'made') ✅\n• Set off (natural phrase) ✅\n• Drove car (not 'conducted') ✅\n• Arrived AT airport (not 'to') ✅\n• On time (punctual) ✅\n• Checked in luggage ✅\n• Boarded plane (not 'entered') ✅\n• Flew to Paris ✅\n• Arrived AT hotel ✅\n• Checked in ✅\n• Stayed for a week ✅\n\n🌟 YOU NOW SOUND NATIVE!\n• All natural collocations mastered ✅\n• No literal translations! ✅\n• Complete travel vocabulary ✅\n• Can navigate airports & hotels ✅\n• Professional travel English! ✅\n\nB1-B2 Travel & Transportation = COMPLETE! 💪🌍\n\n¡Excelente trabajo! Ready for reading! 📖✈️"
        }
      ]
    }
  ],

  // ==========================================================================
  // READING SET - MY TRIP TO EUROPE (5 PASSAGES OF ~40 WORDS)
  // ==========================================================================
  readingSet: {
    id: "m7-u12-reading",
    title: "Reading Practice - My Trip to Europe",
    category: "reading",
    
    questions: [
      // ======================================================================
      // BLOQUE 1: Preparation (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**My Trip to Europe**

**Part 1: Preparation**

Last summer, I planned my dream vacation to Europe! I packed my bags carefully the night before. I needed to arrive at the airport on time because my flight was early. I set off from home at 5 AM, feeling excited about my journey!`
      },
      {
        type: "mc",
        q: "What did the narrator do the night before?",
        options: [
          "Packed bags",
          "Drove to airport",
          "Boarded the plane"
        ],
        a: "Packed bags",
        successtip: "✓ PACK BAGS!\n'Packed bags' = preparation! Collocation used correctly! 🧳"
      },
      {
        type: "blank",
        q: "The narrator wanted to arrive ___ ___. (punctually)",
        a: "on time",
        accept: { ignoreCase: true },
        successtip: "✓ ON TIME!\n'On time' = puntual! Important for flights! ⏰✅"
      },
      {
        type: "mc",
        q: "What phrase means 'begin the journey'?",
        options: [
          "Set off",
          "Pack",
          "Stay"
        ],
        a: "Set off",
        successtip: "✓ SET OFF!\n'Set off' = iniciar viaje! Natural collocation! 🚀"
      },

      // ======================================================================
      // BLOQUE 2: Airport (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 2: At the Airport**

I took a taxi to the airport - it was faster than the bus. After I arrived at the airport, I checked in my luggage at the counter. Then I waited for my flight to board. Finally, they announced boarding, and I boarded the plane excitedly!`
      },
      {
        type: "mc",
        q: "What transportation did the narrator take to the airport?",
        options: [
          "Taxi",
          "Train",
          "Bicycle"
        ],
        a: "Taxi",
        successtip: "✓ TAXI!\nTook a taxi = common for airport! Quick with luggage! 🚕"
      },
      {
        type: "blank",
        q: "Correct preposition: 'Arrived ___ the airport'",
        a: "at",
        accept: { ignoreCase: true },
        successtip: "✓ ARRIVE AT!\nArrive AT (not TO) the airport! Critical preposition! ✈️📍"
      },
      {
        type: "mc",
        q: "What did the narrator do with luggage?",
        options: [
          "Checked in (facturó)",
          "Packed",
          "Threw away"
        ],
        a: "Checked in (facturó)",
        successtip: "✓ CHECK IN LUGGAGE!\nCheck in = facturar! Give bags to airline! 🧳✅"
      },
      {
        type: "blank",
        q: "The narrator ___ the plane when it was time. (abordar)",
        a: "boarded",
        accept: { ignoreCase: true },
        successtip: "✓ BOARDED!\nBoard a plane = abordar! Formal airport term! ✈️"
      },

      // ======================================================================
      // BLOQUE 3: Flight & Arrival (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 3: The Flight**

I flew in a plane for eight hours to reach Paris. The flight was comfortable, and we arrived on time. After landing, I took a taxi to my hotel. I arrived at the hotel tired but happy. The receptionist helped me check into my room quickly!`
      },
      {
        type: "mc",
        q: "How many hours was the flight?",
        options: [
          "Eight hours",
          "Five hours",
          "One hour"
        ],
        a: "Eight hours",
        successtip: "✓ LONG FLIGHT!\nEight hours = transatlantic flight! Long journey! ✈️⏰"
      },
      {
        type: "blank",
        q: "Complete naturally: '___ in a plane' (volar)",
        a: "Flew",
        accept: { ignoreCase: true },
        successtip: "✓ FLY IN A PLANE!\n'Fly in a plane' = natural collocation! ✈️"
      },
      {
        type: "mc",
        q: "Where did the narrator go after landing?",
        options: [
          "To the hotel",
          "Back home",
          "To the airport"
        ],
        a: "To the hotel",
        successtip: "✓ TO HOTEL!\nAfter arrival = go to accommodation! Next step! 🏨"
      },
      {
        type: "blank",
        q: "The narrator ___ ___ the hotel room. (registrarse)",
        a: "checked into",
        accept: { ignoreCase: true, alts: ["checked in to", "checked in"] },
        successtip: "✓ CHECK INTO!\n'Check into' hotel = registrarse! Get room key! 🏨🔑"
      },

      // ======================================================================
      // BLOQUE 4: Hotel Stay (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 4: Hotel Stay**

I stayed at a beautiful hotel near the Eiffel Tower for five days. Every morning, I explored Paris by train and bus. I visited museums, went cycling along the Seine River, and tried amazing French food. The vacation was wonderful - everything went perfectly!`
      },
      {
        type: "mc",
        q: "How long did the narrator stay?",
        options: [
          "Five days",
          "One day",
          "Ten days"
        ],
        a: "Five days",
        successtip: "✓ FIVE DAYS!\n'Stayed for five days' = quedarse! Hotel stay duration! 🏨📅"
      },
      {
        type: "blank",
        q: "The narrator ___ ___ a beautiful hotel. (quedarse en)",
        a: "stayed at",
        accept: { ignoreCase: true, alts: ["stayed in"] },
        successtip: "✓ STAY AT!\n'Stay at/in a hotel' = quedarse! Both prepositions work! 🏨"
      },
      {
        type: "mc",
        q: "What transportation did the narrator use in Paris?",
        options: [
          "Train and bus",
          "Only bicycle",
          "Only taxi"
        ],
        a: "Train and bus",
        successtip: "✓ PUBLIC TRANSPORT!\nTrain + bus = common city transport! Economical! 🚆🚌"
      },
      {
        type: "blank",
        q: "The narrator 'went ___' along the river. (andar en bici - activity)",
        a: "cycling",
        accept: { ignoreCase: true },
        successtip: "✓ GO CYCLING!\n'Go cycling' = andar en bici (activity)! 'Ride a bike' = alternative! 🚴"
      },

      // ======================================================================
      // BLOQUE 5: Return + FINAL (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 5: Returning Home**

On my last day, I checked out of the hotel and took a taxi to the airport. I almost missed my flight because of traffic, but I arrived just in time! I boarded the plane and flew back home. It was an unforgettable vacation - I want to go on another trip soon!`
      },
      {
        type: "mc",
        q: "What did the narrator do on the last day at the hotel?",
        options: [
          "Checked out",
          "Checked in",
          "Stayed longer"
        ],
        a: "Checked out",
        successtip: "✓ CHECK OUT!\n'Check out' = dejar el hotel, pagar! Opposite of check in! 🏨⬅️"
      },
      {
        type: "blank",
        q: "The narrator almost ___ the flight. (perder)",
        a: "missed",
        accept: { ignoreCase: true },
        successtip: "✓ MISS A FLIGHT!\n'Miss a flight' = perder vuelo! Arrived just in time! ✈️😰"
      },
      {
        type: "mc",
        q: "Complete: 'Go ___ another trip' (natural phrase)",
        options: [
          "on",
          "to",
          "in"
        ],
        a: "on",
        successtip: "✓ GO ON!\n'Go on a trip/vacation' = natural collocation! 🏖️✈️"
      },
      {
        type: "mc",
        q: "Throughout the reading, what was demonstrated?",
        options: [
          "All Unit 12 topics: vehicles, places, collocations naturally integrated",
          "Only vocabulary",
          "Only grammar"
        ],
        a: "All Unit 12 topics: vehicles, places, collocations naturally integrated",
        successtip: "🎉 READING COMPLETE! 🎉\n\n📚 YOU SAW IN CONTEXT:\n\n✅ VEHICLES\n• Taxi, plane, train, bus, bicycle ✅\n• All transportation types used! ✅\n\n✅ TRANSIT PLACES\n• Airport mentioned multiple times ✅\n• Hotel as accommodation ✅\n\n✅ NATURAL COLLOCATIONS - ALL!\n• Packed bags (NOT made!) ✅\n• Set off (began journey) ✅\n• Arrived AT airport (NOT to!) ✅\n• On time (punctual) ✅\n• Checked in luggage ✅\n• Boarded plane (NOT entered!) ✅\n• Flew in a plane ✅\n• Arrived AT hotel ✅\n• Checked into hotel ✅\n• Stayed at hotel ✅\n• Went cycling ✅\n• Checked out of hotel ✅\n• Missed/Caught flight ✅\n• Go on a trip ✅\n\n✅ COMPLETE JOURNEY\n• Preparation → Travel → Stay → Return ✅\n• All stages naturally described ✅\n\n✅ NO ERRORS!\n• No 'made bags' ✅\n• No 'conducted car' ✅\n• No 'arrived to' ✅\n• No 'entered plane' ✅\n• All native collocations! ✅\n\n🌟 UNIT 12 IN ACTION!\nComplete travel story with perfect collocations! 🌍✈️🏨"
      }
    ]
  },

  // ==========================================================================
  // REVIEW SET - UNIT 12 INTEGRATION (COMPLETO)
  // ==========================================================================
  reviewSet: {
    id: "m7-u12-review",
    title: "⭐ Unit 12 Review: Travel & Transportation",
    category: "review",
    isReview: true,
    
    questions: [
      {
        type: "mc",
        q: "Which is a water transport vehicle?",
        options: [
          "Ship",
          "Train",
          "Bicycle"
        ],
        a: "Ship",
        successtip: "✓ WATER TRANSPORT!\nShip = barco! Water vessel! Boat, yacht also water! 🚢"
      },
      
      {
        type: "blank",
        q: "Where do planes take off and land? The ___",
        a: "airport",
        accept: { ignoreCase: true },
        successtip: "✓ AIRPORT!\nAirport = aeropuerto! Has terminals, runways! ✈️"
      },
      
      {
        type: "mc",
        q: "Complete naturally: '___ a car' (NOT 'conduct'!)",
        options: [
          "Drive",
          "Ride",
          "Board"
        ],
        a: "Drive",
        successtip: "✓ DRIVE!\nDrive a car! NOT 'conduct' (false friend)! 🚗"
      },
      
      {
        type: "order",
        q: "Complete: '___ on a bus' (subir)",
        a: "Get",
        successtip: "✓ GET ON!\n'Get on' a bus/train! Subir! Natural collocation! 🚌⬆️"
      },
      
      {
        type: "mc",
        q: "Correct preposition: 'Arrive ___ the airport'",
        options: [
          "at (NOT to!)",
          "to",
          "in"
        ],
        a: "at (NOT to!)",
        successtip: "✓ ARRIVE AT!\nArrive AT airport/hotel/station! Critical! NOT 'arrive to'! 📍"
      },
      
      {
        type: "blank",
        q: "At the airport, you '___ ___' your luggage. (facturar)",
        a: "check in",
        accept: { ignoreCase: true },
        successtip: "✓ CHECK IN!\nCheck in luggage = facturar! Give bags to airline! 🧳✅"
      },
      
      {
        type: "mc",
        q: "For planes, the formal word for entering is:",
        options: [
          "Board (abordar)",
          "Get on",
          "Enter"
        ],
        a: "Board (abordar)",
        successtip: "✓ BOARD!\nBoard a plane/ship = abordar! Formal! NOT 'get on' for planes! ✈️"
      },
      
      {
        type: "order",
        q: "Complete: '___ your bags' (before trip - NOT 'make'!)",
        a: "Pack",
        successtip: "✓ PACK!\nPack bags! NOT 'make bags'! Empacar! 🧳"
      },
      
      {
        type: "mc",
        q: "At a hotel, to register and get your room: '___ ___'",
        options: [
          "Check in / Check into",
          "Board",
          "Pack"
        ],
        a: "Check in / Check into",
        successtip: "✓ CHECK IN!\nCheck in(to) hotel = registrarse! Get room key! 🏨🔑"
      },
      
      {
        type: "mc",
        q: "FINAL: Unit 12 mastery includes:",
        options: [
          "13 vehicles + transit places + ALL natural collocations (drive, ride, get on/off, board, arrive AT, check in, pack, set off)",
          "Only vehicles",
          "Only grammar"
        ],
        a: "13 vehicles + transit places + ALL natural collocations (drive, ride, get on/off, board, arrive AT, check in, pack, set off)",
        successtip: "🎉 UNIT 12 MASTERED! 🎉\n\n✅ TRANSPORTATION (13!)\n• LAND: Car, taxi, bus, coach, train, tram, bike, motorcycle ✅\n• AIR: Plane, helicopter ✅\n• WATER: Ship, boat, yacht ✅\n\n✅ TRANSIT PLACES (9!)\n• Bus stop, train station, railway ✅\n• Road, taxi rank/stand ✅\n• Airport, port, terminal, runway ✅\n\n✅ CRITICAL COLLOCATIONS\n• Drive a car (NOT conduct!) ✅\n• Ride a bike (NOT drive!) ✅\n• Get on/off bus (NOT enter/exit!) ✅\n• Board a plane (NOT get on!) ✅\n• Take a taxi/bus/train ✅\n• Fly in a plane ✅\n\n✅ AIRPORT COLLOCATIONS\n• Arrive AT airport (NOT to!) ✅\n• Check in luggage ✅\n• Board a plane ✅\n• Catch/Miss flight ✅\n\n✅ JOURNEY COLLOCATIONS\n• Pack bags (NOT make!) ✅\n• Set off on journey ✅\n• Be on time ✅\n• Go on vacation/holiday ✅\n• Luggage, flight, cruise ✅\n\n✅ HOTEL COLLOCATIONS\n• Arrive AT hotel (NOT to!) ✅\n• Check into hotel ✅\n• Stay in/at hotel ✅\n• Check out of hotel ✅\n• Reception ✅\n\n✅ ACCOMMODATION\n• Hotel vs Hostel ✅\n\n🌟 YOU SOUND NATIVE!\n• No literal translations! ✅\n• All natural collocations! ✅\n• Complete travel vocabulary! ✅\n• Professional travel English! ✅\n\nB1-B2 Travel = COMPLETE! 💪🌍\n\n¡Unit 12 done! Ready for MODULE REVIEW! 🚀✨"
      }
    ]
  }
};
